import { useTranslation } from 'react-i18next';
import { Button, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@monksflow/monks-ui';
import { ChevronRightIcon, ShieldQuestionIcon } from 'lucide-react';
import { JsonView } from '@/shared/ui';
import type { PauseDecisions } from '../model/usePauseDecisions';

export interface ApprovalPromptProps {
  /** The paused turn's decisions, shared with the questions in the input. */
  pause: PauseDecisions;
}

/** What the call would be made with, as the JSON it is. */
function InputSummary({ input }: { input: unknown }) {
  return <JsonView value={input} className="ml-6 max-h-60 rounded-md bg-muted px-sm py-xs" />;
}

/**
 * The tool approvals a paused turn waits on, answered in place. With a single approval and no
 * question, the first click resumes the turn; otherwise it waits for the rest (see
 * `usePauseDecisions`).
 */
export function ApprovalPrompt({ pause }: ApprovalPromptProps) {
  const { t } = useTranslation('chats');
  const { toolApprovals: approvals, sending, failed } = pause;

  if (approvals.length === 0) return null;

  const decide = (approvalId: string, approved: boolean) => {
    pause.decide({ approvalId, approved });
  };

  return (
    <section
      aria-label={t('toolApprovals.approvalPrompt.label')}
      className="flex flex-col gap-sm rounded-lg border border-warning-solid bg-card px-md py-sm"
    >
      {approvals.map((approval, index) => {
        const choice = pause.decided[approval.approvalId]?.approved;
        const isLast = index === approvals.length - 1;
        return (
          <div key={approval.approvalId} className="flex flex-col gap-2xs">
            <div className="flex items-center gap-xs">
              <ShieldQuestionIcon className="size-4 shrink-0 text-warning-foreground" />
              <span className="min-w-0 flex-1 text-body-sm font-medium text-foreground">
                {approval.agentName
                  ? t('toolApprovals.approvalPrompt.title', {
                      agent: approval.agentName,
                      tool: approval.toolLabel,
                    })
                  : t('toolApprovals.approvalPrompt.titleNoAgent', { tool: approval.toolLabel })}
              </span>
            </div>
            <Collapsible className="flex flex-col gap-2xs">
              <CollapsibleTrigger className="group flex items-center gap-2xs self-start pl-6 text-xs text-muted-foreground hover:text-foreground">
                <ChevronRightIcon className="size-3 transition-transform duration-fast ease-out group-data-[state=open]:rotate-90" />
                {t('toolApprovals.approvalPrompt.details')}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <InputSummary input={approval.input} />
              </CollapsibleContent>
            </Collapsible>
            <div className="flex items-center gap-xs pl-6">
              <span className="min-w-0 flex-1 text-xs text-muted-foreground">
                {isLast &&
                  (failed ? (
                    <span className="text-destructive">
                      {t('toolApprovals.approvalPrompt.failed')}
                    </span>
                  ) : (
                    t('toolApprovals.approvalPrompt.hint')
                  ))}
              </span>
              {choice === undefined ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={sending}
                    onClick={() => decide(approval.approvalId, false)}
                  >
                    {t('toolApprovals.approvalPrompt.deny')}
                  </Button>
                  <Button
                    size="sm"
                    disabled={sending}
                    onClick={() => decide(approval.approvalId, true)}
                  >
                    {t('toolApprovals.approvalPrompt.approve')}
                  </Button>
                </>
              ) : (
                <span className="text-body-sm text-muted-foreground">
                  {choice
                    ? t('toolApprovals.approvalPrompt.approved')
                    : t('toolApprovals.approvalPrompt.denied')}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}
